import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Breadcrumbs from '../../components/Breadcrumbs';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { ArrowRight, CheckCircle, Package, Globe } from 'lucide-react';

const ECCNvsHTS = () => {
  const comparisonData = [
    {
      category: "Primary Purpose",
      eccn: "Export control classification for dual-use items and technology",
      hts: "Customs classification for determining import duties and tariffs"
    },
    {
      category: "Governing Authority",
      eccn: "U.S. Department of Commerce - Bureau of Industry and Security (BIS)",
      hts: "U.S. International Trade Commission / Customs and Border Protection"
    },
    {
      category: "Code Format",
      eccn: "5-character alphanumeric (e.g., 3A001, 5D002)",
      hts: "10-digit numeric code (e.g., 8471.30.0100)"
    },
    {
      category: "Classification Basis",
      eccn: "Function, technology level, and reason for control",
      hts: "Physical characteristics, material composition, and use"
    },
    {
      category: "When Required",
      eccn: "For exports from the United States",
      hts: "For imports into the United States"
    },
    {
      category: "Legal Framework",
      eccn: "Export Administration Regulations (EAR)",
      hts: "Harmonized Tariff Schedule of the United States"
    },
    {
      category: "Classification Categories",
      eccn: "10 categories (0-9) covering electronics, materials, software, etc.",
      hts: "99 chapters organized by product type"
    },
    {
      category: "Typical Use Cases",
      eccn: "Export licensing requirements, re-export controls, deemed exports",
      hts: "Import duty calculation, trade statistics, quota administration"
    },
    {
      category: "Level of Restriction",
      eccn: "Varies by destination, end-user, and end-use (can be highly restrictive)",
      hts: "Determines duty rate (can be 0% to 100%+ depending on product)"
    },
    {
      category: "Public Availability",
      eccn: "Commerce Control List published in EAR",
      hts: "Harmonized Tariff Schedule published by USITC"
    },
    {
      category: "Binding Determinations",
      eccn: "Commodity Classification (CCATS) from BIS",
      hts: "Binding Rulings from CBP"
    },
    {
      category: "Penalties for Error",
      eccn: "Can result in export violations and substantial penalties",
      hts: "Can result in underpayment of duties, penalties, and seizure"
    }
  ];

  return (
    <div className="comparison-page">
      <SEO 
        title="ECCN vs HTS: Understanding Export vs Import Classification"
        description="Complete guide to ECCN (Export Control Classification Number) and HTS (Harmonized Tariff Schedule) classification differences. Learn when to use each system."
        keywords="ECCN vs HTS, export classification, import classification, ECCN code, HTS code, product classification, trade compliance"
        path="/eccn-vs-hts"
      />
      <Header />
      <Breadcrumbs items={[
        { label: 'Resources', path: '/resources' },
        { label: 'ECCN vs HTS', path: '/eccn-vs-hts' }
      ]} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl">
            <div className="inline-block h-1 w-16 bg-slate-600 mb-8"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              ECCN vs HTS Classification
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              ECCN and HTS are two distinct classification systems serving different purposes in international trade. Understanding the difference is essential for compliance with both export controls and customs regulations.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Summary */}
      <section className="py-12 bg-white border-y border-gray-200">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <Package className="h-8 w-8 text-blue-600" />
                <h3 className="text-2xl font-bold text-blue-900">ECCN</h3>
              </div>
              <p className="text-blue-800 mb-4 font-semibold">Export Control Classification Number</p>
              <ul className="space-y-2 text-blue-900">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Used for <strong>exports</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Determines export licensing requirements</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Commerce Department jurisdiction</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span>Format: 5 characters (e.g., 3A001)</span>
                </li>
              </ul>
            </div>
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="h-8 w-8 text-green-600" />
                <h3 className="text-2xl font-bold text-green-900">HTS</h3>
              </div>
              <p className="text-green-800 mb-4 font-semibold">Harmonized Tariff Schedule</p>
              <ul className="space-y-2 text-green-900">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Used for <strong>imports</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Determines customs duties and tariffs</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Customs and Border Protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Format: 10 digits (e.g., 8471.30.0100)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Detailed Comparison
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-700 text-white">
                  <th className="text-left p-4 font-bold text-lg">Category</th>
                  <th className="text-left p-4 font-bold text-lg">ECCN</th>
                  <th className="text-left p-4 font-bold text-lg">HTS</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-4 font-semibold text-gray-900 border-b border-gray-200">
                      {row.category}
                    </td>
                    <td className="p-4 text-gray-700 border-b border-gray-200">
                      {row.eccn}
                    </td>
                    <td className="p-4 text-gray-700 border-b border-gray-200">
                      {row.hts}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Key Concepts */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Important to Know
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Do I Need Both Classifications?
              </h3>
              <p className="text-gray-700 mb-4">
                <strong>Yes, if you both import and export.</strong> Many companies need both classifications:
              </p>
              <ul className="space-y-3 text-gray-700 ml-6">
                <li className="flex items-start gap-2">
                  <span className="text-slate-600 font-bold">•</span>
                  <span><strong>HTS code</strong> - Required when importing components or finished goods into the U.S.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-600 font-bold">•</span>
                  <span><strong>ECCN</strong> - Required when exporting those same items or products you manufacture</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Common Classification Mistakes
              </h3>
              <div className="space-y-4 text-gray-700">
                <div className="bg-red-50 border-l-4 border-red-600 p-4">
                  <p className="font-semibold text-red-900 mb-2">Mistake #1: Using HTS for Exports</p>
                  <p className="text-red-800">While Schedule B (export statistics) uses similar codes to HTS, you still need an ECCN for export control purposes.</p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-600 p-4">
                  <p className="font-semibold text-red-900 mb-2">Mistake #2: Using ECCN for Import Duties</p>
                  <p className="text-red-800">An ECCN cannot be used to determine customs duties. You must have a proper HTS classification.</p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-600 p-4">
                  <p className="font-semibold text-red-900 mb-2">Mistake #3: Assuming EAR99 Means No HTS Needed</p>
                  <p className="text-red-800">Even if your product is EAR99 (lowest export control), you still need HTS for imports.</p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-8">
              <h3 className="text-xl font-bold text-amber-900 mb-3">
                Classification Best Practice
              </h3>
              <p className="text-amber-800">
                Maintain both ECCN and HTS classifications in your product database. This ensures you're prepared for both import and export transactions and demonstrates compliance due diligence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-700">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need Help with Product Classification?
          </h2>
          <p className="text-xl text-slate-100 mb-10 max-w-2xl mx-auto">
            Our classification specialists can help determine both ECCN and HTS codes for your products, ensuring compliance with export and import regulations.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://calendly.com/nancy-wood-blackbird/0tg-trade-compliance-services" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-900 px-10 h-14 font-semibold">
                Schedule Classification Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/services/product-classification">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 h-14 font-semibold">
                Learn About Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Related Comparisons</h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/itar-vs-ear" className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow">
              <h4 className="font-bold text-gray-900 mb-2">ITAR vs EAR</h4>
              <p className="text-sm text-gray-600">Export control regulations</p>
            </Link>
            <Link to="/glossary" className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow">
              <h4 className="font-bold text-gray-900 mb-2">Trade Glossary</h4>
              <p className="text-sm text-gray-600">Classification terminology</p>
            </Link>
            <Link to="/faq" className="bg-white rounded-lg p-6 hover:shadow-lg transition-shadow">
              <h4 className="font-bold text-gray-900 mb-2">FAQs</h4>
              <p className="text-sm text-gray-600">Common classification questions</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ECCNvsHTS;
